import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { ToastContext } from './toastContext';
import type { ToastContextValue } from '../types/toastContext';
import {
  DEFAULT_TOAST_DURATION_MS,
  MAX_VISIBLE_TOASTS,
  type Toast,
  type ToastInput,
  type ToastType,
} from '../types/toast';
import { registerToastDispatch } from './toastDispatch';
import { createToastId, hasDuplicateToast } from './toastUtils';

type ToastQueueState = {
  active: Toast[];
  queue: Toast[];
};

function promoteQueuedToast(active: Toast[], queue: Toast[]) {
  if (active.length >= MAX_VISIBLE_TOASTS || queue.length === 0) {
    return { active, queue };
  }

  const [nextToast, ...remainingQueue] = queue;
  return {
    active: [...active, nextToast],
    queue: remainingQueue,
  };
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toastState, setToastState] = useState<ToastQueueState>({
    active: [],
    queue: [],
  });
  const [exitingIds, setExitingIds] = useState<string[]>([]);

  const completeDismiss = useCallback((id: string) => {
    setExitingIds((current) => current.filter((exitingId) => exitingId !== id));
    setToastState((current) => {
      const active = current.active.filter((toast) => toast.id !== id);
      return promoteQueuedToast(active, current.queue);
    });
  }, []);

  const dismissToast = useCallback((id: string) => {
    setExitingIds((current) =>
      current.includes(id) ? current : [...current, id],
    );
  }, []);

  const showToast = useCallback((type: ToastType, input: ToastInput) => {
    const title = input.title.trim();
    const description = input.description?.trim() ?? '';

    if (title === '' && description === '') {
      return undefined;
    }

    const toast: Toast = {
      id: createToastId(),
      type,
      title,
      ...(description !== '' ? { description } : {}),
      ...(input.action != null ? { action: input.action } : {}),
      durationMs:
        input.durationMs === undefined
          ? DEFAULT_TOAST_DURATION_MS
          : input.durationMs,
    };

    let addedToastId: string | undefined;

    setToastState((current) => {
      const allToasts = [...current.active, ...current.queue];

      if (hasDuplicateToast(allToasts, type, title, description || undefined)) {
        return current;
      }

      addedToastId = toast.id;

      if (current.active.length < MAX_VISIBLE_TOASTS) {
        return {
          ...current,
          active: [...current.active, toast],
        };
      }

      return {
        ...current,
        queue: [...current.queue, toast],
      };
    });

    return addedToastId;
  }, []);

  useEffect(() => {
    registerToastDispatch(showToast);
    return () => registerToastDispatch(null);
  }, [showToast]);

  const value = useMemo<ToastContextValue>(
    () => ({
      activeToasts: toastState.active,
      exitingIds,
      showToast,
      dismissToast,
      completeDismiss,
    }),
    [toastState.active, exitingIds, showToast, dismissToast, completeDismiss],
  );

  return (
    <ToastContext.Provider value={value}>{children}</ToastContext.Provider>
  );
}
